import type { ConnectorDefinition } from './types';

/**
 * Single source of truth for connector identity, category, and capabilities.
 * Persisted DataStore.connectorKey values must stay stable — do not rename keys.
 */
export const CONNECTORS: ConnectorDefinition[] = [
  // —— Databases ——
  {
    connectorKey: 'postgres',
    displayName: 'PostgreSQL',
    category: 'databases',
    description: 'Open source relational database',
    status: 'available',
    capabilities: ['ingest', 'tool'],
    aliases: ['PostgreSQL', 'postgres'],
  },
  {
    connectorKey: 'mssql',
    displayName: 'SQL Server / Azure SQL',
    category: 'databases',
    description: 'Microsoft SQL Server and Azure SQL Database',
    status: 'available',
    capabilities: ['ingest', 'tool'],
    aliases: ['SqlServer', 'SQL Server', 'mssql', 'Azure SQL', 'AzureSQL'],
  },
  {
    connectorKey: 'mysql',
    displayName: 'MySQL',
    category: 'databases',
    description: 'Popular open source database',
    status: 'available',
    capabilities: ['ingest', 'tool'],
    aliases: ['MySQL', 'mysql'],
  },
  {
    connectorKey: 'oracle',
    displayName: 'Oracle Database',
    category: 'databases',
    description: 'Enterprise relational database',
    status: 'available',
    capabilities: ['ingest', 'tool'],
    aliases: ['Oracle', 'oracle'],
  },
  {
    connectorKey: 'mongodb',
    displayName: 'MongoDB',
    category: 'databases',
    description: 'Document-oriented NoSQL database',
    status: 'available',
    capabilities: ['ingest', 'tool'],
    aliases: ['NoSQL_MongoDB', 'MongoDB', 'mongodb'],
  },
  {
    connectorKey: 'redis',
    displayName: 'Redis',
    category: 'databases',
    description: 'In-memory data store and cache',
    status: 'available',
    capabilities: ['ingest', 'tool'],
    aliases: ['Redis', 'redis'],
  },
  {
    connectorKey: 'elasticsearch',
    displayName: 'Elasticsearch',
    category: 'databases',
    description: 'Search and analytics engine',
    status: 'available',
    capabilities: ['ingest', 'tool'],
    aliases: ['Elasticsearch', 'elasticsearch'],
  },
  {
    connectorKey: 'snowflake',
    displayName: 'Snowflake',
    category: 'databases',
    description: 'Cloud data warehouse',
    status: 'available',
    capabilities: ['ingest', 'tool'],
    aliases: ['Snowflake', 'snowflake'],
  },
  {
    connectorKey: 'sqlite',
    displayName: 'SQLite',
    category: 'databases',
    description: 'Lightweight embedded database',
    status: 'available',
    capabilities: ['ingest'],
    aliases: ['SQLite'],
  },
  {
    connectorKey: 'firestore',
    displayName: 'Firestore',
    category: 'databases',
    description: 'Google Cloud NoSQL database',
    status: 'available',
    capabilities: ['ingest'],
    aliases: ['Firestore'],
  },

  // —— Cloud storage ——
  {
    connectorKey: 's3',
    displayName: 'Amazon S3',
    category: 'cloud_storage',
    description: 'Amazon cloud object storage',
    status: 'available',
    capabilities: ['ingest', 'storage'],
    aliases: ['S3_Bucket', 'S3', 's3'],
  },
  {
    connectorKey: 'google-drive',
    displayName: 'Google Drive',
    category: 'cloud_storage',
    description: 'Store, share, and collaborate on files',
    status: 'available',
    capabilities: ['ingest', 'storage'],
    aliases: ['Google_Drive', 'GoogleDrive', 'google-drive'],
  },
  {
    connectorKey: 'onedrive',
    displayName: 'OneDrive',
    category: 'cloud_storage',
    description: 'Microsoft cloud file storage',
    status: 'available',
    capabilities: ['ingest', 'storage'],
    aliases: ['OneDrive', 'oneDrive', 'onedrive'],
  },
  {
    connectorKey: 'azure_blob',
    displayName: 'Azure Blob / Data Lake Storage',
    category: 'cloud_storage',
    description: 'Azure Blob Storage and Data Lake Storage Gen2',
    status: 'available',
    capabilities: ['ingest', 'storage'],
    aliases: ['Azure_Blob', 'Azure Blob'],
  },
  {
    connectorKey: 'gcs',
    displayName: 'Google Cloud Storage',
    category: 'cloud_storage',
    description: 'Google cloud object storage',
    status: 'available',
    capabilities: ['ingest', 'storage'],
  },

  // —— File transfer ——
  {
    connectorKey: 'sftp',
    displayName: 'SFTP',
    category: 'file_transfer',
    description: 'Secure file transfer protocol',
    status: 'available',
    capabilities: ['ingest', 'storage'],
    aliases: ['FTP_SFTP', 'SFTP', 'sftp', 'FTP'],
  },

  // —— Messaging ——
  {
    connectorKey: 'slack',
    displayName: 'Slack',
    category: 'messaging',
    description: 'Slack workspace bots and messaging',
    status: 'available',
    capabilities: ['channel', 'tool'],
    aliases: ['Slack', 'slack'],
  },
  {
    connectorKey: 'microsoftTeams',
    displayName: 'Microsoft Teams',
    category: 'messaging',
    description: 'Teams bots and channel messaging',
    status: 'available',
    capabilities: ['channel', 'tool'],
    aliases: ['MicrosoftTeams', 'microsoftTeams', 'Teams'],
    templateId: 'ms-team',
  },
  {
    connectorKey: 'whatsapp',
    displayName: 'WhatsApp Business',
    category: 'messaging',
    description: 'Meta WhatsApp Cloud API messaging',
    status: 'available',
    capabilities: ['channel', 'tool'],
    aliases: ['WhatsApp', 'whatsapp'],
  },
  {
    connectorKey: 'discord',
    displayName: 'Discord',
    category: 'messaging',
    description: 'Discord bot and server integrations',
    status: 'available',
    capabilities: ['channel'],
  },
  {
    connectorKey: 'telegram',
    displayName: 'Telegram',
    category: 'messaging',
    description: 'Telegram bot API',
    status: 'available',
    capabilities: ['channel'],
  },

  // —— Email ——
  {
    connectorKey: 'microsoftOutlook',
    displayName: 'Microsoft Outlook',
    category: 'email',
    description: 'Outlook / Microsoft 365 mail',
    status: 'available',
    capabilities: ['channel', 'tool', 'ingest'],
    aliases: ['MicrosoftOutlook', 'microsoftOutlook', 'Outlook'],
  },
  {
    connectorKey: 'smtp',
    displayName: 'SMTP Mail Server',
    category: 'email',
    description: 'Send email via SMTP',
    status: 'available',
    capabilities: ['tool', 'channel'],
    aliases: ['SMTP', 'smtp'],
  },
  {
    connectorKey: 'imap',
    displayName: 'IMAP Mail Server',
    category: 'email',
    description: 'Read mailboxes via IMAP',
    status: 'available',
    capabilities: ['ingest', 'tool', 'channel'],
    aliases: ['IMAP', 'imap'],
  },

  // —— Collaboration ——
  {
    connectorKey: 'sharepoint',
    displayName: 'SharePoint Online',
    category: 'collaboration',
    description: 'SharePoint sites and document libraries',
    status: 'available',
    capabilities: ['ingest', 'storage', 'tool'],
    aliases: ['SharePoint', 'sharepoint'],
  },
  {
    connectorKey: 'googleWorkspace',
    displayName: 'Google Workspace',
    category: 'collaboration',
    description: 'Gmail, Drive, and Workspace APIs',
    status: 'available',
    capabilities: ['ingest', 'tool', 'channel'],
    aliases: ['GoogleWorkspace', 'googleWorkspace'],
  },
  {
    connectorKey: 'notion',
    displayName: 'Notion',
    category: 'collaboration',
    description: 'Notion workspaces and databases',
    status: 'available',
    capabilities: ['ingest', 'tool'],
  },
  {
    connectorKey: 'confluence',
    displayName: 'Confluence',
    category: 'collaboration',
    description: 'Atlassian Confluence knowledge base',
    status: 'available',
    capabilities: ['ingest', 'tool'],
  },

  // —— CRM ——
  {
    connectorKey: 'salesforce',
    displayName: 'Salesforce',
    category: 'crm_erp',
    description: 'CRM and cloud platform',
    status: 'available',
    capabilities: ['ingest', 'tool'],
    aliases: ['Salesforce', 'salesforce'],
  },
  {
    connectorKey: 'dynamics365',
    displayName: 'Dynamics 365 / Dataverse',
    category: 'crm_erp',
    description: 'Microsoft Dynamics 365 and Dataverse',
    status: 'available',
    capabilities: ['ingest', 'tool'],
    aliases: ['Dynamics', 'Dynamics365', 'dynamics365'],
  },
  {
    connectorKey: 'hubspot',
    displayName: 'HubSpot',
    category: 'crm_erp',
    description: 'CRM, marketing, and service hub',
    status: 'available',
    capabilities: ['ingest', 'tool'],
  },
  {
    connectorKey: 'sap',
    displayName: 'SAP S/4HANA (OData)',
    category: 'crm_erp',
    description: 'SAP S/4HANA and SAP OData services',
    status: 'available',
    capabilities: ['ingest', 'tool'],
    aliases: ['SAP'],
  },
  {
    connectorKey: 'google_sheets',
    displayName: 'Google Sheets',
    category: 'crm_erp',
    description: 'Collaborative spreadsheets',
    status: 'available',
    capabilities: ['ingest', 'tool'],
    aliases: ['Google_Sheets'],
  },

  // —— Social ——
  {
    connectorKey: 'facebook',
    displayName: 'Facebook / Meta',
    category: 'social',
    description: 'Facebook Pages and Meta Graph API',
    status: 'available',
    capabilities: ['channel', 'tool'],
    aliases: ['Facebook', 'Meta', 'facebook'],
  },
  {
    connectorKey: 'instagram',
    displayName: 'Instagram',
    category: 'social',
    description: 'Instagram Business messaging and content',
    status: 'available',
    capabilities: ['channel', 'tool'],
  },
  {
    connectorKey: 'linkedin',
    displayName: 'LinkedIn',
    category: 'social',
    description: 'LinkedIn pages and messaging',
    status: 'available',
    capabilities: ['channel', 'tool'],
  },

  // —— Voice ——
  {
    connectorKey: 'twilio',
    displayName: 'Twilio',
    category: 'voice_telephony',
    description: 'Voice, SMS, and telephony APIs',
    status: 'available',
    capabilities: ['channel', 'tool'],
  },

  // —— APIs ——
  {
    connectorKey: 'api_rest',
    displayName: 'REST API / OpenAPI',
    category: 'apis',
    description: 'Connect to any REST API or OpenAPI-described service',
    status: 'available',
    capabilities: ['ingest', 'tool'],
    aliases: ['API', 'REST', 'api_rest'],
  },
  {
    connectorKey: 'webhook',
    displayName: 'Webhook',
    category: 'apis',
    description: 'Inbound real-time event notifications',
    status: 'available',
    capabilities: ['tool', 'channel'],
    aliases: ['Webhook'],
  },
  {
    connectorKey: 'graphql',
    displayName: 'GraphQL',
    category: 'apis',
    description: 'GraphQL API endpoint',
    status: 'available',
    capabilities: ['ingest', 'tool'],
  },

  // —— Portfolio P0 ——
  {
    connectorKey: 'servicenow',
    displayName: 'ServiceNow',
    category: 'crm_erp',
    description: 'ITSM incidents, requests, and CMDB',
    status: 'available',
    capabilities: ['ingest', 'tool'],
    aliases: ['ServiceNow', 'SNOW'],
  },
  {
    connectorKey: 'jira',
    displayName: 'Jira Software / JSM',
    category: 'collaboration',
    description: 'Jira Software and Jira Service Management',
    status: 'available',
    capabilities: ['ingest', 'tool'],
    aliases: ['Jira', 'JSM'],
  },
  {
    connectorKey: 'databricks',
    displayName: 'Databricks',
    category: 'databases',
    description: 'Databricks lakehouse and SQL warehouses',
    status: 'available',
    capabilities: ['ingest', 'tool'],
    aliases: ['Databricks'],
  },
  {
    connectorKey: 'entra_id',
    displayName: 'Microsoft Entra ID',
    category: 'identity',
    description: 'Microsoft Entra ID (Azure AD) directory and Graph',
    status: 'available',
    capabilities: ['tool', 'ingest'],
    aliases: ['Entra', 'AzureAD', 'Azure AD', 'entra_id'],
  },
  {
    connectorKey: 'okta',
    displayName: 'Okta',
    category: 'identity',
    description: 'Okta identity and access management',
    status: 'available',
    capabilities: ['tool', 'ingest'],
    aliases: ['Okta'],
  },

  // —— Portfolio P1 ——
  {
    connectorKey: 'netsuite',
    displayName: 'Oracle NetSuite',
    category: 'crm_erp',
    description: 'NetSuite ERP via SuiteTalk REST / TBA',
    status: 'available',
    capabilities: ['ingest', 'tool'],
    aliases: ['NetSuite'],
  },
  {
    connectorKey: 'workday',
    displayName: 'Workday',
    category: 'crm_erp',
    description: 'Workday HCM and financials',
    status: 'available',
    capabilities: ['ingest', 'tool'],
    aliases: ['Workday'],
  },
  {
    connectorKey: 'oracle_fusion',
    displayName: 'Oracle Fusion Cloud ERP',
    category: 'crm_erp',
    description: 'Oracle Fusion Cloud ERP REST services',
    status: 'available',
    capabilities: ['ingest', 'tool'],
    aliases: ['OracleFusion', 'Fusion ERP'],
  },
  {
    connectorKey: 'powerbi',
    displayName: 'Power BI',
    category: 'observability',
    description: 'Power BI workspaces, datasets, and reports',
    status: 'available',
    capabilities: ['ingest', 'tool'],
    aliases: ['PowerBI', 'Power BI'],
  },

  // —— Portfolio P2 ——
  {
    connectorKey: 'splunk',
    displayName: 'Splunk',
    category: 'observability',
    description: 'Splunk search, alerts, and HEC',
    status: 'available',
    capabilities: ['ingest', 'tool'],
    aliases: ['Splunk'],
  },
  {
    connectorKey: 'sentinel',
    displayName: 'Microsoft Sentinel',
    category: 'observability',
    description: 'Microsoft Sentinel / Log Analytics SOC',
    status: 'available',
    capabilities: ['ingest', 'tool'],
    aliases: ['Sentinel', 'Microsoft Sentinel'],
  },
  {
    connectorKey: 'palo_alto',
    displayName: 'Palo Alto Networks / Panorama',
    category: 'observability',
    description: 'Panorama and Palo Alto firewall management',
    status: 'available',
    capabilities: ['ingest', 'tool'],
    aliases: ['PaloAlto', 'Panorama'],
  },
  {
    connectorKey: 'amazon_sp_api',
    displayName: 'Amazon Seller Central / SP-API',
    category: 'crm_erp',
    description: 'Amazon Selling Partner API for marketplace sellers',
    status: 'available',
    capabilities: ['ingest', 'tool'],
    aliases: ['AmazonSP', 'Seller Central', 'SP-API'],
  },
];
